import { useRef, useState } from 'react';
import { EyeOff, ImagePlus, Pencil, Plus, Sparkles, Trash2, X } from 'lucide-react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import { GST_RATES } from '../../utils/gst';
import form from '../../styles/form.module.css';
import styles from './CategoryDrawer.module.css';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const PROTECTED_CATEGORY_ID = 'general';

const EMOJI_OPTIONS = ['🌾', '🫘', '🫗', '🥛', '🥚', '🥭', '🧅', '🍚', '🍪', '🧂', '🧴', '🍞', '🧃', '📦', '🛒', '🧊'];
const COLOR_OPTIONS = ['#F2B922', '#2E7D32', '#2F6FED', '#7C3AED', '#0D9488', '#D9724B', '#B8850A', '#D32F2F'];

export default function CategoryDrawer({
  open, onClose, categories, categoriesLoading, categoriesError, productCounts,
  onAdd, onUpdate, onToggleStatus, onOpenReassign,
  togglingCategoryId, actionError,
  inUseConflict, onClearInUseConflict,
}) {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState(EMOJI_OPTIONS[0]);
  const [color, setColor] = useState(COLOR_OPTIONS[0]);
  const [defaultGstRate, setDefaultGstRate] = useState(GST_RATES[0]);
  const [sortOrder, setSortOrder] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [hadImage, setHadImage] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const imageRef = useRef(null);

  const tileFor = (c) => c.imageUrl || '';
  const isProtected = (id) => String(id).toLowerCase() === PROTECTED_CATEGORY_ID;

  const resetForm = () => {
    setEditingId(null);
    setName('');
    setEmoji(EMOJI_OPTIONS[0]);
    setColor(COLOR_OPTIONS[0]);
    setDefaultGstRate(GST_RATES[0]);
    setSortOrder('');
    setImageUrl('');
    setImageFile(null);
    setHadImage(false);
    setError('');
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setName(c.name);
    setEmoji(c.emoji);
    setColor(c.color);
    setDefaultGstRate(c.defaultGstRate ?? GST_RATES[0]);
    setSortOrder(c.sortOrder === 0 || c.sortOrder ? String(c.sortOrder) : '');
    setImageUrl(c.imageUrl || '');
    setImageFile(null);
    setHadImage(!!c.imageUrl);
    onClearInUseConflict?.();
  };

  const handleTileFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      setError('Choose a JPEG or PNG image.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError('Tile image must be 5 MB or smaller.');
      return;
    }
    setError('');
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImageUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setError('');
    setSubmitting(true);
    try {
      const sort = sortOrder === '' ? undefined : Number(sortOrder);
      const removeImage = hadImage && !imageUrl && !imageFile;
      if (editingId) {
        await onUpdate(editingId, {
          name: name.trim(),
          emoji,
          color,
          sortOrder: sort,
          imageFile,
          removeImage,
        });
      } else {
        await onAdd({
          name: name.trim(),
          emoji,
          color,
          defaultGstRate: Number(defaultGstRate),
          sortOrder: sort,
          imageFile,
        });
      }
      resetForm();
    } catch (err) {
      setError(err.message || `Could not ${editingId ? 'update' : 'create'} this category. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const conflictCat = inUseConflict
    ? categories.find((c) => c.id === inUseConflict.id)
    : null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Categories"
      width={420}
    >
      <form onSubmit={handleSubmit} className={styles.onboardForm}>
        <div className={styles.formLabelRow}>
          <p className={styles.formLabel}>
            {editingId ? <><Pencil size={13} /> Edit category</> : <><Sparkles size={13} /> Onboard a new category</>}
          </p>
          {editingId && (
            <button type="button" className={styles.cancelEditBtn} onClick={resetForm}>
              <X size={12} /> Cancel
            </button>
          )}
        </div>

        <AuthMessage>{error}</AuthMessage>

        <p className={form.label} style={{ marginBottom: 8 }}>Category tile image</p>
        <div
          className={styles.tileDropzone}
          onClick={() => imageRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          {imageUrl ? (
            <>
              <img src={imageUrl} alt="" className={styles.tileImg} />
              <button
                type="button"
                className={styles.tileRemove}
                onClick={(e) => {
                  e.stopPropagation();
                  setImageUrl('');
                  setImageFile(null);
                }}
                aria-label="Remove tile image"
              >
                <X size={12} />
              </button>
            </>
          ) : (
            <>
              <ImagePlus size={20} />
              <span>Upload tile image</span>
            </>
          )}
        </div>
        <p className={form.hint} style={{ marginTop: -8, marginBottom: 16 }}>
          Shown on the customer category grid. JPEG or PNG, up to 5 MB.
        </p>
        <input ref={imageRef} type="file" accept="image/*" hidden onChange={handleTileFile} />

        <label className={form.field}>
          <span className={form.label}>Category name</span>
          <input className={form.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Bakery & Breads" />
        </label>

        <label className={form.field}>
          <span className={form.label}>Sort order</span>
          <input
            className={form.input}
            type="number"
            min="0"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            placeholder="0"
          />
          <span className={form.hint}>Lower numbers appear first on the storefront.</span>
        </label>

        <p className={form.label} style={{ marginBottom: 8 }}>Icon</p>
        <div className={styles.emojiGrid}>
          {EMOJI_OPTIONS.map((e) => (
            <button
              type="button"
              key={e}
              className={`${styles.emojiOption} ${emoji === e ? styles.emojiOptionActive : ''}`}
              onClick={() => setEmoji(e)}
            >
              {e}
            </button>
          ))}
        </div>

        <p className={form.label} style={{ margin: '14px 0 8px' }}>Accent color</p>
        <div className={styles.colorRow}>
          {COLOR_OPTIONS.map((c) => (
            <button
              type="button"
              key={c}
              className={`${styles.colorOption} ${color === c ? styles.colorOptionActive : ''}`}
              style={{ background: c }}
              onClick={() => setColor(c)}
              aria-label={c}
            />
          ))}
        </div>

        <Button type="submit" icon={editingId ? Pencil : Plus} disabled={!name.trim() || submitting} className={styles.addBtn}>
          {submitting ? 'Adding…' : editingId ? 'Save changes' : 'Add category'}
        </Button>
      </form>

      <div className={styles.divider} />

      <AuthMessage>{actionError}</AuthMessage>

      {inUseConflict && (
        <div className={styles.inUseBanner} role="alert">
          <p className={styles.inUseTitle}>
            Can&apos;t delete{conflictCat ? ` “${conflictCat.name}”` : ''}
          </p>
          <p className={styles.inUseBody}>
            Move {inUseConflict.productCount} product{inUseConflict.productCount === 1 ? '' : 's'} first.
            Reassign them to another ACTIVE category, then delete.
          </p>
          <div className={styles.inUseActions}>
            <Button
              type="button"
              size="sm"
              onClick={() => onOpenReassign?.(inUseConflict.id)}
            >
              Reassign products
            </Button>
            {conflictCat?.status === 'ACTIVE' && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                icon={EyeOff}
                disabled={togglingCategoryId === inUseConflict.id}
                onClick={async () => {
                  const ok = await onToggleStatus(inUseConflict.id);
                  if (ok !== false) onClearInUseConflict?.();
                }}
              >
                Hide instead
              </Button>
            )}
            <button type="button" className={styles.inUseDismiss} onClick={() => onClearInUseConflict?.()}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {categoriesLoading ? (
        <p className={styles.listLabel}>Loading categories…</p>
      ) : categoriesError ? (
        <AuthMessage>{categoriesError}</AuthMessage>
      ) : (
        <>
          <p className={styles.listLabel}>{categories.length} categories</p>
          <div className={styles.list}>
            {categories.map((c) => {
              const count = productCounts[c.id] || 0;
              const toggling = togglingCategoryId === c.id;
              const protectedCat = isProtected(c.id);
              const tile = tileFor(c);
              return (
                <div
                  key={c.id}
                  className={`${styles.row} ${editingId === c.id ? styles.rowEditing : ''} ${inUseConflict?.id === c.id ? styles.rowConflict : ''}`}
                >
                  <span className={styles.rowSwatch} style={{ background: `${c.color}22`, color: c.color }}>
                    {tile ? <img src={tile} alt="" className={styles.rowSwatchImg} /> : c.emoji}
                  </span>
                  <div className={styles.rowInfo}>
                    <p className={styles.rowName}>{c.name}</p>
                    <p className={styles.rowMeta}>
                      {count} product{count === 1 ? '' : 's'}
                      {c.sortOrder !== undefined && c.sortOrder !== '' && ` · sort ${c.sortOrder}`}
                      {c.defaultGstRate !== undefined && ` · ${c.defaultGstRate}% GST`}
                    </p>
                  </div>
                  <span className={`${styles.statusChip} ${c.status === 'ACTIVE' ? styles.active : styles.hidden}`}>
                    {c.status === 'ACTIVE' ? 'Visible' : 'Hidden'}
                  </span>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={() => startEdit(c)}
                    aria-label="Edit category"
                    title="Edit category"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    className={styles.iconBtn}
                    onClick={() => onToggleStatus(c.id)}
                    disabled={toggling}
                    aria-label={c.status === 'ACTIVE' ? 'Hide category' : 'Unhide category'}
                    title={c.status === 'ACTIVE' ? 'Hide from storefront' : 'Show on storefront'}
                  >
                    <EyeOff size={13} />
                  </button>
                  {!protectedCat && (
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => {
                        onClearInUseConflict?.();
                        onOpenReassign?.(c.id);
                      }}
                      disabled={toggling}
                      aria-label="Move products and delete category"
                      title="Move products and delete category"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </Drawer>
  );
}
